import { TFunction } from 'i18next'

import {
  addDays,
  format,
  parseISO,
  getISOWeek,
  parse,
  isSameDay,
  isValid,
  startOfDay,
} from 'date-fns'
import { enUS, ro } from 'date-fns/locale'

export const formatDate = (date: Date | undefined): string | undefined => {
  if (!date) return
  return format(date, 'yyyy-MM-dd')
}

/** Local calendar date of an instant (e.g. a pass expiry), without the time. */
export const toDisplayLocalDate = (
  date: string,
  language: string
): string | undefined => {
  if (!date) return
  return format(date, 'd MMM yyyy', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export const toDisplayTimeAndDate = (
  date: string,
  language: string
): string | undefined => {
  if (!date) return
  return format(date, 'd MMM yyyy, HH:mm', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export const formatDateTimesInText = (text: string, language: string): string =>
  text.replace(
    /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?/g,
    value => {
      const date = parseISO(value)
      return Number.isNaN(date.getTime())
        ? value
        : (toDisplayTimeAndDate(value, language) ?? value)
    }
  )

export const toDisplayOnlyTime = (dateTime?: string): string => {
  if (!dateTime) return ''
  const parts = dateTime.split('T')
  if (parts.length < 2) return ''
  const timePart = parts[1]
  const [hours, minutes] = timePart.split(':')
  if (!hours || !minutes) return ''
  return `${hours}:${minutes}`
}

export const toDisplayOnlyDate = (
  dateTime: string | undefined,
  language: string
): string => {
  if (!dateTime) return ''
  const datePart = dateTime.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  const parsedDate = new Date(year, month - 1, day)
  return format(parsedDate, 'd MMM yyyy', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export const DayOfWeekOrder: DayOfWeek[] = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
]

export const haveSameDays = (
  days: DayOfWeek[],
  expectedDays: DayOfWeek[]
): boolean =>
  days.length === expectedDays.length &&
  expectedDays.every(day => days.includes(day))

export const toDisplayMonthYear = (
  date: Date | string,
  language: string
): string => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date

  return format(parsedDate, 'MMMM yyyy', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export const today = (): string => {
  return format(new Date(), 'yyyy-MM-dd')
}

export const toWeek = (date?: string | Date): string => {
  if (!date) {
    return ''
  }

  return getISOWeek(new Date(date)).toString()
}

export const displayShortMonth = (
  date: string | Date,
  language: string
): string => {
  const value = typeof date === 'string' ? parseISO(date) : date
  return format(value, 'MMM', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export const displayDay = (date: string): string => {
  return format(date, 'd')
}

export const displayShortDayOfWeek = (
  date: string | Date,
  language: string
): string => {
  const value = typeof date === 'string' ? parseISO(date) : date
  return format(value, 'EEE', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export const displayWeekdayMonthDay = (
  date: string,
  language: string
): string => {
  return format(date, 'EEEE, MMMM d', {
    locale: language === 'ro' ? ro : enUS,
  })
}

export const displayDate = (date: Date | string, language?: string): string => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return language
    ? format(parsedDate, 'yyyy-MM-dd', {
        locale: language === 'ro' ? ro : enUS,
      })
    : format(parsedDate, 'yyyy-MM-dd')
}

export const formatAvailableDay = (value: DayOfWeek, t: TFunction): string => {
  return t(`common:days-short.${value.toLowerCase()}`)
}

export const displayHourMinute = (time: string): string => {
  return format(parse(time, 'HH:mm:ss', new Date()), 'HH:mm')
}

export const displayFullHour = (hour: number): string =>
  `${String(hour).padStart(2, '0')}:00`

export const generateTimeOptions = (
  openTime: string,
  closeTime: string,
  stepMinutes: number
): string[] => {
  const toMinutes = (time: string) => {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  const openMinutes = toMinutes(openTime)
  const closeMinutes = toMinutes(closeTime)

  if (
    !Number.isFinite(openMinutes) ||
    !Number.isFinite(closeMinutes) ||
    stepMinutes <= 0 ||
    openMinutes >= closeMinutes
  ) {
    return []
  }

  const options: string[] = []

  for (
    let currentMinutes = openMinutes;
    currentMinutes <= closeMinutes;
    currentMinutes += stepMinutes
  ) {
    const hours = Math.floor(currentMinutes / 60)
    const minutes = currentMinutes % 60
    options.push(
      `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    )
  }

  return options
}

export const toValidDate = (value?: Date | string | null): Date | null => {
  if (!value) return null

  const date = typeof value === 'string' ? parseISO(value) : value
  return isValid(date) ? date : null
}

export const toTimeValue = (value?: string | null): string => {
  if (!value) return ''

  const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (timeMatch) {
    return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
  }

  return toDisplayOnlyTime(value)
}

export const combineDateAndTime = (date: Date | string, time: string): string =>
  `${displayDate(date)}T${/^\d{2}:\d{2}$/.test(time) ? `${time}:00` : time}`

export const combineDateAndOptionalTime = (
  date: Date | string,
  time?: string
): string => (time ? combineDateAndTime(date, time) : displayDate(date))

export const replaceDateKeepingTime = (value: string, date: Date): string =>
  combineDateAndTime(date, toTimeValue(value) || '00:00')

export const replaceTimeKeepingDate = (value: string, time: string): string => {
  const date = toValidDate(value)
  return date ? combineDateAndTime(date, time) : time
}

export const toLocalDateTime = (date?: Date | string | null): string => {
  if (!date) {
    return ''
  }

  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss")
}

export const displayLocalDateTimeHourMinute = (dateTime: string): string => {
  return format(parseISO(dateTime), 'HH:mm')
}

export const getCurrentYear = () => new Date().getFullYear()

export const getDays = (startDate: Date, numberOfDays: number): Date[] =>
  Array.from({ length: numberOfDays }, (_, index) => addDays(startDate, index))

export const isSameDate = (
  date1: Date | string,
  date2: Date | string
): boolean =>
  isSameDay(
    typeof date1 === 'string' ? parseISO(date1) : date1,
    typeof date2 === 'string' ? parseISO(date2) : date2
  )

export const getStartOfDay = (date: Date): Date => {
  return startOfDay(date)
}

export const getStartOfToday = (): Date => {
  return getStartOfDay(new Date())
}

export type DateRange = {
  start: Date
  end: Date
  startDate: string
  endDate: string
  days: Date[]
}

export const getDateRange = (start: Date, numberOfDays: number): DateRange => {
  if (numberOfDays < 1) {
    throw new RangeError('numberOfDays must be greater than 0')
  }

  const days = getDays(start, numberOfDays)
  const end = days[days.length - 1]

  return {
    start,
    end,
    days,
    startDate: displayDate(start),
    endDate: displayDate(end),
  }
}

export const getDateRangeFromToday = (numberOfDays: number): DateRange => {
  return getDateRange(getStartOfToday(), numberOfDays)
}

/** Time left until {@code targetDate} as whole days, hours (0-23) and minutes (0-59). */
export const getCountdown = (targetDate?: string) => {
  const parsed = targetDate ? toValidDate(targetDate) : null
  const diff = parsed ? parsed.getTime() - Date.now() : 0
  const totalMinutes = diff > 0 ? Math.floor(diff / 60000) : 0

  return {
    days: Math.floor(totalMinutes / (24 * 60)),
    hours: Math.floor(totalMinutes / 60) % 24,
    minutes: totalMinutes % 60,
  }
}
